export const MAX_USER_PHOTOS = 6;

const INVALID_VALUES = new Set([
  'null',
  'undefined',
  'none',
  'delete',
  'deleted',
  'false',
  '0',
  'no-image.jpg',
  'default.jpg',
  'default.png',
  'default.webp',
]);

export const toAbsolute = (path) => {
  if (!path) return '';
  const value = String(path).trim();
  if (!value) return '';
  if (INVALID_VALUES.has(value.toLowerCase())) return '';

  // Base API URLs derived from environment or defaults
  const rawApiBase = process.env.REACT_APP_API_BASE_URL || 'https://hindusanjog.com/api/v1/';
  const domainBase = rawApiBase
    .replace(/\/api\/v1\/?$/i, '')
    .replace(/\/api\/v2\/?$/i, '')
    .replace(/\/+$/, '');

  const v1Base = domainBase ? `${domainBase}/api/v1/` : 'https://hindusanjog.com/api/v1/';
  const v2PublicBase = domainBase ? `${domainBase}/api/v2/public/` : 'https://hindusanjog.com/api/v2/public/';

  // Fix full URLs pointing to wrong api/v1 path for admin uploads (hs_ or assets/users/)
  if (/^https?:\/\//i.test(value)) {
    if (value.includes('/assets/users/') || /\/hs_\d+_\d+_\d+/.test(value)) {
      const filename = value.split('/').pop();
      return `${v2PublicBase}assets/users/${filename}`;
    }
    return value;
  }

  if (
    value.startsWith('data:') ||
    value.startsWith('blob:') ||
    value.startsWith('static/')
  ) {
    return value;
  }

  // Remove leading slash
  const cleanPath = value.replace(/^\/+/, '');

  // 1. Direct match or bare filename for admin panel (v2) user uploads
  if (
    cleanPath.startsWith('assets/users/') ||
    cleanPath.startsWith('public/assets/users/') ||
    cleanPath.startsWith('hs_')
  ) {
    const filename = cleanPath.split('/').pop();
    return `${v2PublicBase}assets/users/${filename}`;
  }

  // 2. Direct match or bare filename for legacy (v1) user uploads
  if (
    cleanPath.startsWith('images/users/') ||
    cleanPath.startsWith('public/images/users/') ||
    cleanPath.startsWith('hh') ||
    cleanPath.startsWith('girl-') ||
    cleanPath.startsWith('Anirudh_') ||
    cleanPath.startsWith('gemstones-')
  ) {
    const filename = cleanPath.split('/').pop();
    return `${v1Base}images/users/${filename}`;
  }

  // 3. Generic assets path (v2)
  if (cleanPath.startsWith('public/assets/')) {
    return `${v2PublicBase}${cleanPath.replace(/^public\//, '')}`;
  }
  if (cleanPath.startsWith('assets/')) {
    return `${v2PublicBase}${cleanPath}`;
  }

  // 4. Generic images path (v1)
  if (cleanPath.startsWith('public/images/')) {
    return `${v1Base}${cleanPath.replace(/^public\//, '')}`;
  }
  if (cleanPath.startsWith('images/')) {
    return `${v1Base}${cleanPath}`;
  }

  // Default fallback
  return `${v1Base}${cleanPath}`;
};

/**
 * The API stores photos in flat columns image1..image6, pp, photo, profile_photo, or images/photos arrays.
 * Returns them as an ordered list of unique absolute URLs, primary photo first.
 */
export const getUserImages = (user) => {
  if (!user) return [];
  const urls = [];
  const addUrl = (p) => {
    const abs = toAbsolute(p);
    if (abs && !urls.includes(abs)) {
      urls.push(abs);
    }
  };

  // Primary profile picture fields
  if (user.pp) addUrl(user.pp);
  if (user.profile_pic) addUrl(user.profile_pic);
  if (user.photo) addUrl(user.photo);
  if (user.profile_photo) addUrl(user.profile_photo);
  if (user.user_image) addUrl(user.user_image);
  if (user.avatar) addUrl(user.avatar);
  if (user.img) addUrl(user.img);

  // Flat image columns image1 .. image6
  for (let i = 1; i <= MAX_USER_PHOTOS; i += 1) {
    if (user[`image${i}`]) {
      addUrl(user[`image${i}`]);
    }
  }

  // Handle arrays or comma-separated images/photos fields
  const arrayCandidates = [user.images, user.photos, user.gallery];
  arrayCandidates.forEach((item) => {
    if (Array.isArray(item)) {
      item.forEach((entry) => {
        if (typeof entry === 'string') addUrl(entry);
        else if (entry && typeof entry === 'object') {
          addUrl(entry.url || entry.path || entry.src || entry.image);
        }
      });
    } else if (typeof item === 'string' && item.includes(',')) {
      item.split(',').forEach((s) => addUrl(s.trim()));
    }
  });

  return urls;
};

export const getPrimaryUserImage = (user, fallback = '') => getUserImages(user)[0] || fallback;


